import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { GetPayslipEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { PaymentFrequencyType } from '@nxsys/types/models/Common';
import { getOptionsAllTaxWeekTaxMonth } from '@nxsys/utils/taxYear';
import { Form, Typography } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useRef } from 'react';

const payslipStatusOptions = [
  { label: 'Upcoming', value: 'AFTER_DRAFT_PAYROLL' },
  { label: 'Paid', value: 'AFTER_CONFIRM_PAYMENT' },
];

type FilterValues = {
  startDate?: string;
  endDate?: string;
  taxPeriodFrom?: string;
  taxPeriodTo?: string;
  status?: string;
};

type PayslipFilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onFilter: (params: Partial<GetPayslipEmployeeListParams>) => void;
  payFrequency?: PaymentFrequencyType;
  appliedFilters?: FilterValues;
};

const PayslipFilterModal: React.FC<PayslipFilterModalProps> = ({
  isOpen,
  toggle,
  onFilter,
  payFrequency = 'WEEKLY',
  appliedFilters,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const prevIsOpenRef = useRef(false);

  // Reset form to last applied filters when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      form.setFieldsValue({
        startDate: appliedFilters?.startDate
          ? moment(appliedFilters.startDate, 'MM/DD/YYYY')
          : undefined,
        endDate: appliedFilters?.endDate
          ? moment(appliedFilters.endDate, 'MM/DD/YYYY')
          : undefined,
        taxPeriodFrom: appliedFilters?.taxPeriodFrom || undefined,
        taxPeriodTo: appliedFilters?.taxPeriodTo || undefined,
        status: appliedFilters?.status || undefined,
      });
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);
  const ableToClearFilter =
    watchValue && Object.keys(watchValue).some((key) => !!watchValue[key]);

  const taxPeriodOptions = useMemo(
    () =>
      getOptionsAllTaxWeekTaxMonth(payFrequency).map((opt) => ({
        label: opt.label,
        value: String(opt.value),
      })),
    [payFrequency],
  );

  const taxPeriodFromOptions = useMemo(() => {
    const toValue = watchValue?.taxPeriodTo
      ? Number(watchValue.taxPeriodTo)
      : null;
    if (toValue === null) return taxPeriodOptions;
    return taxPeriodOptions.filter((opt) => Number(opt.value) <= toValue);
  }, [taxPeriodOptions, watchValue?.taxPeriodTo]);

  const taxPeriodToOptions = useMemo(() => {
    const fromValue = watchValue?.taxPeriodFrom
      ? Number(watchValue.taxPeriodFrom)
      : null;
    if (fromValue === null) return taxPeriodOptions;
    return taxPeriodOptions.filter((opt) => Number(opt.value) >= fromValue);
  }, [taxPeriodOptions, watchValue?.taxPeriodFrom]);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    const params: Partial<GetPayslipEmployeeListParams> = {
      startDate: values.startDate
        ? moment(values.startDate).format('MM/DD/YYYY')
        : undefined,
      endDate: values.endDate
        ? moment(values.endDate).format('MM/DD/YYYY')
        : undefined,
      taxPeriodFrom: values.taxPeriodFrom || undefined,
      taxPeriodTo: values.taxPeriodTo || undefined,
      status: values.status || undefined,
    };
    onFilter(params);
    toggle();
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Typography.Text>{messages['common.paymentDate']}</Typography.Text>
        <div className="grid grid-cols-2 gap-x-4 mt-2">
          <Form.Item name="startDate">
            <AppDatePicker
              placeholder={messages['common.fromDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({ startDate: date });
                if (
                  date &&
                  watchValue?.endDate &&
                  moment(date).isAfter(watchValue.endDate)
                ) {
                  form.setFieldsValue({ endDate: undefined });
                }
              }}
              format="DD/MM/YYYY"
              maxDate={watchValue?.endDate}
            />
          </Form.Item>
          <Form.Item name="endDate">
            <AppDatePicker
              placeholder={messages['common.toDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({ endDate: date });
                if (
                  date &&
                  watchValue?.startDate &&
                  moment(date).isBefore(watchValue.startDate)
                ) {
                  form.setFieldsValue({ startDate: undefined });
                }
              }}
              format="DD/MM/YYYY"
              minDate={watchValue?.startDate}
            />
          </Form.Item>
        </div>

        <Typography.Text>{messages['common.taxPeriod']}</Typography.Text>
        <div className="grid grid-cols-2 gap-x-4 mt-2">
          <Form.Item name="taxPeriodFrom">
            <AppSelect
              options={taxPeriodFromOptions}
              onChange={(value: { value: string }) => {
                form.setFieldsValue({ taxPeriodFrom: value?.value });
              }}
              placeholder={messages['common.from']}
              allowClear
            />
          </Form.Item>
          <Form.Item name="taxPeriodTo">
            <AppSelect
              options={taxPeriodToOptions}
              onChange={(value: { value: string }) => {
                form.setFieldsValue({ taxPeriodTo: value?.value });
              }}
              placeholder={messages['common.to']}
              allowClear
            />
          </Form.Item>
        </div>

        <Form.Item name="status" label={messages['common.status']}>
          <AppSelect
            options={payslipStatusOptions}
            onChange={(value: { value: string }) => {
              form.setFieldsValue({ status: value?.value });
            }}
            placeholder={messages['form.selectStatus']}
            allowClear
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default PayslipFilterModal;
